import { useTranslation, Trans } from 'react-i18next';

// Import components
import { MoreInfoTooltip } from 'components/views/offer_configurator/components/common';

// Import helpers
import { convertPriceToReadableFormat } from 'helpers/prices';
import { getNextBillingDate, getLongestPromotionDays } from 'components/views/offer_configurator/helpers/basket';

// Import styles
import { Total, Info, Wrapper } from './styles';
import { Price } from 'components/views/offer_configurator/components/common/PriceInfo/styles';

// Import types
import type { Summary } from 'types/views/configurator';

type Props = {
	duration?: string;
	inAdvance: boolean;
	summary: Summary;
};

export const TotalPrice = ({
	duration,
	inAdvance,
	summary: { current_price, standard_price, next_price, billing_period_end_on, products }
}: Props) => {
	const { t } = useTranslation('configurator');
	const price = convertPriceToReadableFormat(current_price);

	if (current_price < standard_price) {
		const nextPrice = convertPriceToReadableFormat(standard_price);
		const days = getLongestPromotionDays(products);
		const endOfLongestPromotion = getNextBillingDate(days + 1);

		return (
			<>
				<Total>
					<Info>
						<p>{t('summary_basket_total_today')}</p>
					</Info>
					<Price isPromotion>{t('price', { price })}</Price>
				</Total>

				<Wrapper>
					<p>{t('summary_basket_standard_price', { date: endOfLongestPromotion })}</p>
					<p>
						<Trans ns="configurator" i18nKey="price_with_duration" values={{ price: nextPrice, duration }} />
					</p>
				</Wrapper>
			</>
		);
	}

	if (current_price !== next_price) {
		const nextPrice = convertPriceToReadableFormat(next_price);
		const nextBilling = getNextBillingDate(1, billing_period_end_on);
		const info = inAdvance ? 'price_in_advance_tooltip' : 'price_before_advance_tooltip';

		return (
			<>
				<Total>
					<Info>
						<p>{t('summary_basket_total_today')}</p>

						<MoreInfoTooltip i18nKey={info} namespace="configurator" />
					</Info>
					<Price>{t('price', { price })}</Price>
				</Total>

				<Wrapper>
					<p>{t('summary_basket_next_price', { date: nextBilling })}</p>
					<p>
						<Trans ns="configurator" i18nKey="price_with_duration" values={{ price: nextPrice, duration }} />
					</p>
				</Wrapper>
			</>
		);
	}

	return (
		<Total>
			<Info>
				<p>{t('summary_basket_total')}</p>
			</Info>
			<Price>
				<Trans ns="configurator" i18nKey="price_with_duration" values={{ price, duration }} />
			</Price>
		</Total>
	);
};
